import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Globe, Send } from 'lucide-react';
import { useState } from 'react';

export function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    message: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    alert('感谢您的咨询！我们将尽快与您联系。');
    setFormData({ name: '', company: '', phone: '', email: '', message: '' });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: '重庆总部',
      content: '重庆市沙坪坝区土主镇永泰路18号华科蔚来云著9栋7-7',
      color: 'from-orange-600 to-yellow-400',
    },
    {
      icon: Phone,
      title: '服务热线',
      content: '+86(023)68141839',
      color: 'from-red-600 to-orange-400',
    },
    {
      icon: Mail,
      title: '电子邮箱',
      content: 'contact@yuegeen.com',
      color: 'from-yellow-600 to-orange-400',
    },
    {
      icon: Globe,
      title: '官方网站',
      content: 'www.yuegeen.com',
      color: 'from-orange-500 to-red-500',
    },
  ];

  return (
    <section id="contact" className="py-16 sm:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <div className="inline-block px-4 py-1 rounded-full bg-orange-50 text-orange-600 text-sm mb-4">
            联系我们
          </div>
          <h2 className="text-gray-900 mb-4 px-4">开启合作之旅</h2>
          <p className="text-gray-600 max-w-2xl mx-auto px-4">
            我们期待与您交流，为您的业务提供专业的数字化解决方案
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <div className="space-y-6">
              {contactInfo.map((info, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start space-x-4 p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br ${info.color} flex items-center justify-center`}>
                    <info.icon className="text-white" size={24} />
                  </div>
                  <div>
                    <div className="text-gray-900 mb-1">{info.title}</div>
                    <div className="text-gray-600">{info.content}</div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Service Commitment */}
            <div className="p-6 rounded-xl bg-gradient-to-br from-yellow-500 via-orange-500 to-red-500 text-white">
              <h4 className="text-white mb-4">服务承诺</h4>
              <ul className="space-y-2 text-white/90 text-sm">
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                  <span>7×24小时响应机制</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                  <span>年度SLA服务协议</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                  <span>专业技术支持团队</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-white"></div>
                  <span>定期客户回访服务</span>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 mb-2">姓名</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">公司名称</label>
                  <input
                    type="text"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="请输入公司名称"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-6">
                <div>
                  <label className="block text-gray-700 mb-2">联系电话</label>
                  <input
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="请输入联系电话"
                  />
                </div>
                <div>
                  <label className="block text-gray-700 mb-2">电子邮箱</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all"
                    placeholder="请输入电子邮箱"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-700 mb-2">咨询内容</label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-500 focus:ring-2 focus:ring-orange-200 outline-none transition-all resize-none"
                  placeholder="请描述您的需求..."
                />
              </div>

              <button
                type="submit"
                className="w-full py-4 rounded-lg bg-gradient-to-r from-yellow-500 via-orange-500 to-red-500 text-white hover:shadow-lg transition-all flex items-center justify-center space-x-2 group"
              >
                <span>提交咨询</span>
                <Send size={20} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}